import { Typography } from '@snack-uikit/typography';
import cn from 'classnames';
import { MouseEventHandler } from 'react';

import { LinkWrapper } from '@/components/static';
import { DYNAMIC_CONTENT_CLASS_NAMES } from '@/constant/dynamic';
import { DATA_TEST_IDS_INJECTORS } from '@/constant/tests';
import { useLayoutCtx } from '@/hooks/ctx';
import { usePathResolver } from '@/hooks/navigation';
import { isEmpty } from '@/utils/common';
import {
  compareUrls,
  getUrlAnchor,
  removeAnchorFromUrlString,
  scrollToAnchor,
  skipFileExtension,
} from '@/utils/navigation';
import { skipHashTag } from '@/utils/strings';

import { classNameIs, findElements } from '../utils';
import { WithGlossaryTooltip } from '../utils';
import s from './styles.module.scss';
import { makeInjector, nodeNameIs } from './utils';

export const aToNextLinkInjector = makeInjector((node, children) => {
  const href = node.attribs.href;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { resolvedPath, isExternal, currentPathName } = usePathResolver(href || '');
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { layoutWrapperRef } = useLayoutCtx();

  // TODO remove skipFileExtension after ext removed from builder
  const cleanResolvedPath = skipFileExtension('html', resolvedPath);
  const glossaryItems = findElements(children, classNameIs(DYNAMIC_CONTENT_CLASS_NAMES.glossaryItem));

  const skipGlossaryTooltip = isEmpty(glossaryItems);

  const isAnchor = skipGlossaryTooltip && href?.includes('#');

  if (isExternal)
    return (
      <a
        data-test-id={DATA_TEST_IDS_INJECTORS.link}
        target={'_blank'}
        className={cn(s.link_wrapper, s.remove_text_decoration)}
        href={resolvedPath}
      >
        <WithGlossaryTooltip pathToGlossary={resolvedPath} skipTooltip={skipGlossaryTooltip}>
          {children}
        </WithGlossaryTooltip>
      </a>
    );

  const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = event => {
    if (isAnchor && compareUrls(removeAnchorFromUrlString(cleanResolvedPath), currentPathName)) {
      event.preventDefault();
      scrollToAnchor(skipHashTag(getUrlAnchor(cleanResolvedPath)), { layoutWrapperRef });
    }
    return;
  };

  return (
    <LinkWrapper
      data-test-id={DATA_TEST_IDS_INJECTORS.link}
      passHref={isExternal}
      externalOnClickLink={handleLinkClick}
      textWrapper={({ children }) => (
        <Typography.SansBodyL
          data-test-id={DATA_TEST_IDS_INJECTORS.link}
          className={cn(s.link_wrapper, { [s.link_dash]: !skipGlossaryTooltip })}
        >
          {children}
        </Typography.SansBodyL>
      )}
      link={cleanResolvedPath}
    >
      <WithGlossaryTooltip pathToGlossary={cleanResolvedPath} skipTooltip={skipGlossaryTooltip}>
        {children}
      </WithGlossaryTooltip>
    </LinkWrapper>
  );
}, nodeNameIs('a'));
